import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import GitHubIcon from '@mui/icons-material/GitHub';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DiffEditor } from '@monaco-editor/react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// -------------------------- DARK THEME SETUP --------------------------
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    },
    text: {
      primary: '#ffffff'
    }
  }
});

// -------------------------- DUMMY DATA --------------------------
// Dummy file tree data (emulating an Arduino LED Blink project)
const dummyFileList = [
  {
    id: "folder-1",
    name: "src",
    type: "folder",
    children: [
      { id: "file-1", name: "main.cpp", type: "file", path: "src/main.cpp" },
      { id: "file-2", name: "utils.cpp", type: "file", path: "src/utils.cpp" }
    ]
  },
  {
    id: "folder-2",
    name: "lib",
    type: "folder",
    children: [
      { id: "file-3", name: "LedBlink.cpp", type: "file", path: "lib/LedBlink.cpp" }
    ]
  },
  { id: "file-4", name: "README.md", type: "file", path: "README.md" }
];

// Dummy code diff data mapping file paths to content (before and after)
const codeDiffData = {
  "src/main.cpp": {
    original: `
#include <stdio.h>
int main() {
    printf("Hello, Arduino!\\n");
    return 0;
}
    `,
    modified: `
#include <stdio.h>
#define GREETING "Hello, Arduino!"
int main() {
    printf(GREETING);
    return 0;
}
    `
  },
  "src/utils.cpp": {
    original: `
// Utility functions
int add(int a, int b) {
    return a+b;
}
    `,
    modified: `
// Utility functions
int add(int a, int b) {
    // Added error checking
    return a + b;
}
    `
  },
  "lib/LedBlink.cpp": {
    original: `
#include <Arduino.h>
void setup() {
    pinMode(13, OUTPUT);
}
void loop() {
    digitalWrite(13, HIGH);
    delay(1000);
    digitalWrite(13, LOW);
    delay(1000);
}
    `,
    modified: `
#include <Arduino.h>
#define BLINK_DELAY 1000
void setup() {
    pinMode(13, OUTPUT);
}
void loop() {
    digitalWrite(13, HIGH);
    delay(BLINK_DELAY);
    digitalWrite(13, LOW);
    delay(BLINK_DELAY);
}
    `
  },
  "README.md": {
    original: `
# LED Blink Project
This is a simple LED blink project.
    `,
    modified: `
# LED Blink Project
This is a simple LED blink project with improvements.
    `
  }
};

// Dummy issues data used to highlight lines in the diff viewer
const issuesData = [
  {
    id: 1,
    description: "Magic number used",
    beforeLine: 4,
    afterLine: 5,
    beforeMessage: "MISRA C violation: Magic number used.",
    afterMessage: "Replaced magic number with a defined constant."
  },
  {
    id: 2,
    description: "Missing null check",
    beforeLine: 6,
    afterLine: 7,
    beforeMessage: "MISRA C violation: Missing null pointer check.",
    afterMessage: "Added null pointer check."
  }
];

// -------------------------- CODE DIFF COMPONENT --------------------------
const CodeDiffWithHover = ({ originalCode, modifiedCode, issues }) => {
  const handleEditorDidMount = (editor, monaco) => {
    const originalEditor = editor.getOriginalEditor();
    const modifiedEditor = editor.getModifiedEditor();

    const beforeDecorations = issues.map(issue => ({
      range: new monaco.Range(issue.beforeLine, 1, issue.beforeLine, 1),
      options: {
        isWholeLine: true,
        className: 'beforeLineHighlight',
        hoverMessage: { value: issue.beforeMessage }
      }
    }));

    const afterDecorations = issues.map(issue => ({
      range: new monaco.Range(issue.afterLine, 1, issue.afterLine, 1),
      options: {
        isWholeLine: true,
        className: 'afterLineHighlight',
        hoverMessage: { value: issue.afterMessage }
      }
    }));

    originalEditor.deltaDecorations([], beforeDecorations);
    modifiedEditor.deltaDecorations([], afterDecorations);
  };

  return (
    <Paper elevation={4} sx={{ height: '85vh', borderRadius: 2, overflow: 'hidden' }}>
      <DiffEditor
        original={originalCode}
        modified={modifiedCode}
        language="cpp"
        onMount={handleEditorDidMount}
        options={{
          renderSideBySide: true,
          ignoreTrimWhitespace: true,
          automaticLayout: true
        }}
        theme="vs-dark"
        height={'100%'}
      />
    </Paper>
  );
};

// -------------------------- FILE TREE (Explorer) --------------------------
const FileTree = ({ fileTree = [], onFileSelect }) => {
  // Recursively render TreeItems using SimpleTreeView and TreeItem.
  const renderTreeItems = (items) => {
    return items.map((item) => (
      <TreeItem
        key={item.id}
        itemId={item.id}
        label={item.name}
        onClick={() => {
          if (item.type === 'file') {
            onFileSelect(item);
          }
        }}
      >
        {item.children && renderTreeItems(item.children)}
      </TreeItem>
    ));
  };

  return (
    <SimpleTreeView>
      {renderTreeItems(fileTree)}
    </SimpleTreeView>
  );
};

const ExplorerPanel = ({ fileTree, onFileSelect }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ color: 'text.primary' }}>Explorer</Typography>
    <FileTree fileTree={fileTree} onFileSelect={onFileSelect} />
  </Box>
);

const SourceControlPanel = () => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ color: 'text.primary' }}>Source Control</Typography>
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
      Dummy Git version control panel.
    </Typography>
    <List>
      <ListItem>
        <ListItemText primary="src/main.cpp modified" sx={{ color: 'text.primary' }} />
      </ListItem>
      <ListItem>
        <ListItemText primary="lib/LedBlink.cpp added" sx={{ color: 'text.primary' }} />
      </ListItem>
    </List>
  </Box>
);

const SearchPanel = () => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ color: 'text.primary' }}>Search</Typography>
    <TextField fullWidth variant="outlined" placeholder="Search files..." InputProps={{ sx: { color: 'white' } }} />
    <List>
      <ListItem>
        <ListItemText primary="Result 1" sx={{ color: 'text.primary' }} />
      </ListItem>
      <ListItem>
        <ListItemText primary="Result 2" sx={{ color: 'text.primary' }} />
      </ListItem>
    </List>
  </Box>
);

// -------------------------- EXTENSIONS SIDEBARS --------------------------
// ExtensionsBar receives controlsAlignment as a prop and sets its border accordingly.
const ExtensionsBar = ({ selectedExtension, onSelectExtension, controlsAlignment, onToggleAlignment }) => {
  const icons = [
    { key: 'explorer', icon: <FolderIcon /> },
    { key: 'search', icon: <SearchIcon /> },
    { key: 'source', icon: <GitHubIcon /> }
  ];

  return (
    <Box
      sx={{
        width: 60,
        bgcolor: 'grey.900',
        color: 'grey.100',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        ...(controlsAlignment === 'right'
          ? { borderLeft: '1px solid #444' }
          : { borderRight: '1px solid #444' })
      }}
    >
      {icons.map(({ key, icon }) => (
        <IconButton
          key={key}
          color={selectedExtension === key ? 'primary' : 'default'}
          onClick={() => onSelectExtension(key)}
          sx={{ mb: 1 }}
        >
          {icon}
        </IconButton>
      ))}
      {/* Toggle button to change panel alignment */}
      <Box sx={{ mt: 'auto' }}>
        <IconButton onClick={onToggleAlignment} sx={{ mb: 1 }}>
          {controlsAlignment === 'right' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

// ExtensionPanel adjusts its border based on controlsAlignment.
const ExtensionPanel = ({ selectedExtension, fileTree, onFileSelect, controlsAlignment }) => {
  let content = null;
  if (selectedExtension === 'explorer') {
    content = <ExplorerPanel fileTree={fileTree} onFileSelect={onFileSelect} />;
  } else if (selectedExtension === 'search') {
    content = <SearchPanel />;
  } else if (selectedExtension === 'source') {
    content = <SourceControlPanel />;
  }
  return (
    <Box
      sx={{
        width: 300,
        bgcolor: 'background.default',
        ...(controlsAlignment === 'right'
          ? { borderLeft: '1px solid #444' }
          : { borderRight: '1px solid #444' }),
        overflow: 'auto',
        color: 'text.primary'
      }}
    >
      {content}
    </Box>
  );
};

// -------------------------- MAIN APP --------------------------
const CodeComparisonApp = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedExtension, setSelectedExtension] = useState(null);
  // Read alignment from local storage (default to 'right')
  const storedAlignment = localStorage.getItem('controlsAlignment') || 'right';
  const [controlsAlignment, setControlsAlignment] = useState(storedAlignment);

  // When clicking an icon in the ExtensionsBar, toggle the panel.
  const handleSelectExtension = (ext) => {
    setSelectedExtension(selectedExtension === ext ? null : ext);
  };

  // Toggle the side alignment and store it in local storage.
  const handleToggleAlignment = () => {
    const newAlignment = controlsAlignment === 'right' ? 'left' : 'right';
    setControlsAlignment(newAlignment);
    localStorage.setItem('controlsAlignment', newAlignment);
  };

  const currentCodeDiff =
    selectedFile && codeDiffData[selectedFile.path]
      ? codeDiffData[selectedFile.path]
      : null;

  // Layout: if controlsAlignment is 'left', show sidebars on the left; if 'right', on the right.
  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          display: 'flex',
          height: `calc(100vh - 64px)`,
          bgcolor: 'background.default',
          borderTop: '2px solid #444'
        }}
      >
        {controlsAlignment === 'left' && (
          <>
            {/* For left alignment, render ExtensionsBar first on the left */}
            <ExtensionsBar
              selectedExtension={selectedExtension}
              onSelectExtension={handleSelectExtension}
              controlsAlignment={controlsAlignment}
              onToggleAlignment={handleToggleAlignment}
            />
            {selectedExtension && (
              <ExtensionPanel
                selectedExtension={selectedExtension}
                fileTree={dummyFileList}
                onFileSelect={setSelectedFile}
                controlsAlignment={controlsAlignment}
              />
            )}
          </>
        )}

        {/* Main Code Diff Area */}
        <Box sx={{ flexGrow: 1, p: 2 }}>
          {selectedFile && currentCodeDiff ? (
            <Box>
              <Typography variant="h5" sx={{ color: 'text.primary', fontSize: '20px' }} gutterBottom>
                {selectedFile.path}
              </Typography>
              <CodeDiffWithHover
                originalCode={currentCodeDiff.original}
                modifiedCode={currentCodeDiff.modified}
                issues={issuesData}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Select a file from the Explorer to view code diff.
              </Typography>
            </Box>
          )}
        </Box>
        {controlsAlignment === 'right' && (
          <>
            {selectedExtension && (
              <ExtensionPanel
                selectedExtension={selectedExtension}
                fileTree={dummyFileList}
                onFileSelect={setSelectedFile}
                controlsAlignment={controlsAlignment}
              />
            )}
            <ExtensionsBar
              selectedExtension={selectedExtension}
              onSelectExtension={handleSelectExtension}
              controlsAlignment={controlsAlignment}
              onToggleAlignment={handleToggleAlignment}
            />
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default CodeComparisonApp;
